import { TransactionResponse } from '@ethersproject/abstract-provider'
import { BigNumber } from '@ethersproject/bignumber'
import { Percent } from '@uniswap/sdk-core'
import { FlatFeeOptions, SwapRouter, UNIVERSAL_ROUTER_ADDRESS } from '@uniswap/universal-router-sdk'
import { FeeOptions, toHex } from '@uniswap/v3-sdk'
import { useTotalBalancesUsdForAnalytics } from 'graphql/data/apollo/TokenBalancesProvider'
import { useEthersWeb3Provider } from 'hooks/useEthersProvider'
import { useGetTransactionDeadline } from 'hooks/useTransactionDeadline'
import { t } from 'i18n'
import useBlockNumber from 'lib/hooks/useBlockNumber'
import { useCallback } from 'react'
import { ClassicTrade, TradeFillType } from 'state/routing/types'
import { useUserSlippageTolerance } from 'state/user/hooks'
import { trace } from 'tracing/trace'
import { calculateGasMargin } from 'utils/calculateGasMargin'
import { UserRejectedRequestError, WrongChainError } from 'utils/errors'
import isZero from 'utils/isZero'
import { didUserReject, swapErrorToUserReadableMessage } from 'utils/swapErrorToUserReadableMessage'
import { useAccount } from 'wagmi'
import { PermitSignature } from './usePermitAllowance'
import NutsJSON from '../constants/Nuts.json'
import Nuts404JSON from '../constants/Nuts404.json'
import { ethers } from 'ethers'

/** Thrown when gas estimation fails. This class of error usually requires an emulator to determine the root cause. */
class GasEstimationError extends Error {
  constructor() {
    super(t`Your swap is expected to fail.`)
  }
}

/**
 * Thrown when the user modifies the transaction in-wallet before submitting it.
 * In-wallet calldata modification nullifies any safeguards (eg slippage) from the interface, so we recommend reverting them immediately.
 */
class ModifiedSwapError extends Error {
  constructor() {
    super(
      t`Your swap was modified through your wallet. If this was a mistake, please cancel immediately or risk losing your funds.`
    )
  }
}

interface SwapOptions {
  slippageTolerance: Percent
  permit?: PermitSignature
  feeOptions?: FeeOptions
  flatFeeOptions?: FlatFeeOptions
}

export function useUniversalRouterSwapCallback(
  trade: ClassicTrade | undefined,
  fiatValues: { amountIn?: number; amountOut?: number; feeUsd?: number },
  options: SwapOptions
) {
  const account = useAccount()
  const provider = useEthersWeb3Provider()
  const connectorName = useAccount().connector?.name

  const blockNumber = useBlockNumber()
  const getDeadline = useGetTransactionDeadline()
  const isAutoSlippage = useUserSlippageTolerance()[0] === 'auto'
  const portfolioBalanceUsd = useTotalBalancesUsdForAnalytics()

  return useCallback(
    (): Promise<{ type: TradeFillType.Classic; response: TransactionResponse; deadline?: BigNumber }> =>
      trace({ name: 'Swap (Classic)', op: 'swap.classic' }, async (trace) => {
        try {
          if (account.status !== 'connected') throw new Error('wallet not connected')
          if (!provider) throw new Error('missing provider')
          if (!trade) throw new Error('missing trade')
          const connectedChainId = await provider.getSigner().getChainId()
          if (account.chainId !== connectedChainId) throw new WrongChainError()

          const deadline = await getDeadline()

          trace.setData('slippageTolerance', options.slippageTolerance.toFixed(2))
          const { calldata: data, value } = SwapRouter.swapERC20CallParameters(trade, {
            slippageTolerance: options.slippageTolerance,
            deadlineOrPreviousBlockhash: deadline?.toString(),
            inputTokenPermit: options.permit,
            fee: options.feeOptions,
            flatFee: options.flatFeeOptions,
          })
          const tx = {
            from: account.address,
            to: UNIVERSAL_ROUTER_ADDRESS(account.chainId),
            data,
            // TODO(https://github.com/Uniswap/universal-router-sdk/issues/113): universal-router-sdk returns a non-hexlified value.
            ...(value && !isZero(value) ? { value: toHex(value) } : {}),
          }

          let gasLimit: BigNumber
          try {
            const gasEstimate = await provider.estimateGas(tx)
            gasLimit = calculateGasMargin(gasEstimate)
            trace.setData('gasLimit', gasLimit.toNumber())
          } catch (gasError) {
            console.warn(gasError)
            throw new GasEstimationError()
          }

          const response = await trace.child(
            { name: 'Send transaction', op: 'wallet.send_transaction' },
            async (walletTrace) => {
              try {
                return await provider.getSigner().sendTransaction({ ...tx, gasLimit })
              } catch (error) {
                if (didUserReject(error)) {
                  walletTrace.setStatus('cancelled')
                  throw new UserRejectedRequestError(swapErrorToUserReadableMessage(error))
                } else {
                  throw error
                }
              }
            }
          )
          
          if (tx.data !== response.data) {
            if (!response.data || response.data.length === 0 || response.data === '0x') {
              throw new ModifiedSwapError()
            }
          }
          return { type: TradeFillType.Classic as const, response, deadline }
        } catch (error: unknown) {
          if (error instanceof GasEstimationError) {
            throw error
          } else if (error instanceof UserRejectedRequestError) {
            trace.setStatus('cancelled')
            throw error
          } else if (error instanceof ModifiedSwapError) {
            trace.setError(error, 'data_loss')
            throw error
          } else {
            trace.setError(error)
            throw Error(swapErrorToUserReadableMessage(error))
          }
        }
      }),
    [
      account.status,
      account.chainId,
      account.address,
      provider,
      trade,
      getDeadline,
      options.slippageTolerance,
      options.permit,
      options.feeOptions,
      options.flatFeeOptions,
      fiatValues,
      portfolioBalanceUsd,
      connectorName,
      blockNumber,
      isAutoSlippage,
    ]
  )
}

export function useUniversalMintCallback(
  nutsAmount: string | undefined
) {
  const account = useAccount()
  const provider = useEthersWeb3Provider()

  return useCallback(
    (): Promise<{ type: TradeFillType.Classic; response: TransactionResponse; deadline?: BigNumber }> =>
      trace({ name: 'Swap (Classic)', op: 'swap.classic' }, async (trace) => {
        try {

          if (account.status !== 'connected') throw new Error('wallet not connected')
          if (!provider) throw new Error('missing provider')
          if (!nutsAmount) throw new Error('missing NUTS amount')
          const connectedChainId = await provider.getSigner().getChainId()
          if (account.chainId !== connectedChainId) throw new WrongChainError()

          const response = await trace.child(
            { name: 'Send transaction', op: 'wallet.send_transaction' },
            async (walletTrace) => {
              try {
                const contractAddress = "0x88266f9eb705F5282a2507A9c418821a2AC9f8BD";
                const signer = provider.getSigner()
                const contract = new ethers.Contract(contractAddress, NutsJSON.abi, signer)
                return await contract.connect(signer).mint(nutsAmount);
              } catch (error) {
                console.error(error)
                if (didUserReject(error)) {
                  walletTrace.setStatus('cancelled')
                  throw new UserRejectedRequestError(swapErrorToUserReadableMessage(error))
                } else {
                  throw error
                }
              }
            }
          )
          
          
          return { type: TradeFillType.Classic as const, response }

        } catch (error: unknown) {
          console.error(error)
          if (error instanceof UserRejectedRequestError) {
            trace.setStatus('cancelled')
            throw error
          } else {
            trace.setError(error)
            throw Error(swapErrorToUserReadableMessage(error))
          }
        }
      }),
    [
      account.status,
      account.chainId,
      account.address,
      provider,
      nutsAmount,
    ]
  )
}

export function useUniversalWrapDepositCallback(
  nutsIds: string | undefined
) {
  const account = useAccount()
  const provider = useEthersWeb3Provider()

  return useCallback(
    (): Promise<{ type: TradeFillType.Classic; response: TransactionResponse; deadline?: BigNumber }> =>
      trace({ name: 'Swap (Classic)', op: 'swap.classic' }, async (trace) => {
        try {

          if (account.status !== 'connected') throw new Error('wallet not connected')
          if (!provider) throw new Error('missing provider')
          if (!nutsIds) throw new Error('missing NUTS ids')
          const connectedChainId = await provider.getSigner().getChainId()
          if (account.chainId !== connectedChainId) throw new WrongChainError()

          const response = await trace.child(
            { name: 'Send transaction', op: 'wallet.send_transaction' },
            async (walletTrace) => {
              try {
                const contractAddress = "0x25559f0aBBaf2A928239D2F419181147Cc2dAd74";
                const signer = provider.getSigner()
                const contract = new ethers.Contract(contractAddress, Nuts404JSON.abi, signer)
                return await contract.connect(signer).deposit(nutsIds.split(","));
              } catch (error) {
                console.error(error)
                if (didUserReject(error)) {
                  walletTrace.setStatus('cancelled')
                  throw new UserRejectedRequestError(swapErrorToUserReadableMessage(error))
                } else {
                  throw error
                }
              }
            }
          )
          
          
          return { type: TradeFillType.Classic as const, response }

        } catch (error: unknown) {
          console.error(error)
          if (error instanceof UserRejectedRequestError) {
            trace.setStatus('cancelled')
            throw error
          } else {
            trace.setError(error)
            throw Error(swapErrorToUserReadableMessage(error))
          }
        }
      }),
    [
      account.status,
      account.chainId,
      account.address,
      provider,
      nutsIds,
    ]
  )
}

export function useUniversalWrapWithdrawCallback(
  nuts404Amount: string | undefined
) {
  const account = useAccount()
  const provider = useEthersWeb3Provider()

  return useCallback(
    (): Promise<{ type: TradeFillType.Classic; response: TransactionResponse; deadline?: BigNumber }> =>
      trace({ name: 'Swap (Classic)', op: 'swap.classic' }, async (trace) => {
        try {

          if (account.status !== 'connected') throw new Error('wallet not connected')
          if (!provider) throw new Error('missing provider')
          if (!nuts404Amount) throw new Error('missing NUTS404 amount')
          const connectedChainId = await provider.getSigner().getChainId()
          if (account.chainId !== connectedChainId) throw new WrongChainError()

            

          const response = await trace.child(
            { name: 'Send transaction', op: 'wallet.send_transaction' },
            async (walletTrace) => {
              try {
                const contractAddress = "0x25559f0aBBaf2A928239D2F419181147Cc2dAd74";
                const signer = provider.getSigner()
                const contract = new ethers.Contract(contractAddress, Nuts404JSON.abi, signer)
                return await contract.connect(signer).withdraw(nuts404Amount + "000000000000000000"); // we add 18 '0' due to the unit input
              } catch (error) {
                console.error(error)
                if (didUserReject(error)) {
                  walletTrace.setStatus('cancelled')
                  throw new UserRejectedRequestError(swapErrorToUserReadableMessage(error))
                } else {
                  throw error
                }
              }
            }
          )
          
          
          return { type: TradeFillType.Classic as const, response }

        } catch (error: unknown) {
          console.error(error)
          if (error instanceof UserRejectedRequestError) {
            trace.setStatus('cancelled')
            throw error
          } else {
            trace.setError(error)
            throw Error(swapErrorToUserReadableMessage(error))
          }
        }
      }),
    [
      account.status,
      account.chainId,
      account.address,
      provider,
      nuts404Amount,
    ]
  )
}